import React, { useState, useEffect } from 'react';
import { Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

interface BMIState {
  weight: string;
  height: string;
  age: string;
  gender: 'male' | 'female';
  result: number | null;
  category: string;
}

function BMICalculator() {
  const [values, setValues] = useState<BMIState>({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    result: null,
    category: ''
  });

  const calculateBMI = () => {
    const weight = parseFloat(values.weight);
    const height = parseFloat(values.height) / 100; // convert cm to meters
    
    if (isNaN(weight) || isNaN(height) || height === 0) {
      return null;
    }

    return weight / (height * height);
  };

  const getBMICategory = (bmi: number): string => {
    if (bmi < 18.5) return 'Pothranjenost';
    if (bmi < 25) return 'Normalna težina';
    if (bmi < 30) return 'Prekomjerna težina';
    if (bmi < 35) return 'Pretilost (stupanj I)';
    if (bmi < 40) return 'Pretilost (stupanj II)';
    return 'Pretilost (stupanj III)';
  };

  const getHealthRisk = (bmi: number): string => {
    if (bmi < 18.5) return 'Povećan rizik od zdravstvenih problema';
    if (bmi < 25) return 'Nizak rizik od zdravstvenih problema';
    if (bmi < 30) return 'Umjeren rizik od zdravstvenih problema';
    if (bmi < 35) return 'Visok rizik od zdravstvenih problema';
    if (bmi < 40) return 'Vrlo visok rizik od zdravstvenih problema';
    return 'Ekstremno visok rizik od zdravstvenih problema';
  };

  const handleInputChange = (field: keyof BMIState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null,
      category: ''
    }));
  };

  const handleCalculate = () => {
    const bmi = calculateBMI();
    if (bmi !== null) {
      const roundedBMI = parseFloat(bmi.toFixed(1));
      setValues(prev => ({
        ...prev,
        result: roundedBMI,
        category: getBMICategory(roundedBMI)
      }));
    }
  };

  useEffect(() => {
    if (values.weight && values.height) {
      handleCalculate();
    }
  }, [values.weight, values.height]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "BMI Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni BMI kalkulator za brz i precizan izračun indeks tjelesne mase kod muške i ženske osobe. Saznaj svoju BMI kategoriju.",
    "url": "https://online-kalkulator.com/bmi-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Automatski BMI kalkulator",
      "Saznaj BMI kategorije",
      "Podržava dob i spol osobe",
      "Automatsko ažuriranje rezultata",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="BMI Kalkulator | Izračun Indeksa Tjelesne Mase"
        description="Izračunajte svoj indeks tjelesne mase (BMI) pomoću našeg besplatnog kalkulatora. Saznajte svoju idealnu težinu i kategoriju težine."
        canonicalUrl="/bmi-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Scale className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">BMI Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Težina (kg)
                  </label>
                  <input
                    type="number"
                    value={values.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 70"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 175"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dob
                  </label>
                  <input
                    type="number"
                    value={values.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol
                  </label>
                  <select
                    value={values.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value as 'male' | 'female')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="male">Muško</option>
                    <option value="female">Žensko</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {values.result !== null && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      BMI: {values.result}
                    </div>
                    <div className="text-lg font-semibold text-gray-700">
                      Kategorija: {values.category}
                    </div>
                    <div className="text-gray-600 mt-2">
                      {getHealthRisk(values.result)}
                    </div>
                  </div>

                  {/* BMI Scale Visualization */}
                  <div className="mt-6">
                    <div className="h-4 rounded-full bg-gradient-to-r from-blue-500 via-green-500 via-yellow-500 to-red-500">
                      <div 
                        className="w-2 h-6 bg-black translate-y-[-4px] relative"
                        style={{ 
                          marginLeft: `${Math.min(Math.max((values.result / 50) * 100, 0), 100)}%`,
                          transition: 'margin-left 0.3s ease-in-out'
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>15</span>
                      <span>25</span>
                      <span>35</span>
                      <span>45+</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što je BMI?</h2>
                <p className="text-gray-600 leading-relaxed">
                  Indeks tjelesne mase (BMI) je jednostavan alat koji se koristi za procjenu uhranjenosti
                  osobe. Izračunava se dijeljenjem težine osobe u kilogramima s kvadratom visine u metrima.
                  BMI je koristan pokazatelj, ali nije jedini faktor koji treba uzeti u obzir pri procjeni
                  zdravlja.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">BMI Kategorije</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="font-medium">Pothranjenost:</span>
                    <span>BMI &lt; 18.5</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">Normalna težina:</span>
                    <span>BMI 18.5 - 24.9</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="font-medium">Prekomjerna težina:</span>
                    <span>BMI 25 - 29.9</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span className="font-medium">Pretilost (stupanj I):</span>
                    <span>BMI 30 - 34.9</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">Pretilost (stupanj II):</span>
                    <span>BMI 35 - 39.9</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-100 rounded">
                    <span className="font-medium">Pretilost (stupanj III):</span>
                    <span>BMI ≥ 40</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti BMI Kalkulator</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite svoju težinu u kilogramima</li>
                  <li>Unesite svoju visinu u centimetrima</li>
                  <li>Unesite svoju dob (opcionalno)</li>
                  <li>Odaberite svoj spol (opcionalno)</li>
                  <li>BMI će se automatski izračunati</li>
                  <li>Pogledajte rezultat i pripadajuću kategoriju</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Ograničenja BMI Kalkulatora</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Ne uzima u obzir sastav tijela (mišići vs. masno tkivo)</li>
                  <li>Može biti neprecizan za sportaše i vrlo mišićave osobe</li>
                  <li>Ne uzima u obzir raspodjelu masnog tkiva</li>
                  <li>Može biti manje precizan za starije osobe</li>
                  <li>Ne uzima u obzir spol i dob (iako su važni faktori)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Zdravu Težinu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Održavajte uravnoteženu prehranu</li>
                  <li>Redovito vježbajte (barem 150 minuta tjedno)</li>
                  <li>Pijte dovoljno vode</li>
                  <li>Spavajte 7-9 sati dnevno</li>
                  <li>Izbjegavajte prekomjerni unos procesiranih namirnica</li>
                  <li>Konzultirajte se s liječnikom prije značajnih promjena u prehrani ili vježbanju</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BMICalculator;